/**
 * Class to handle drop events and add files to the uploader
 */
export class DropHandler {
	/**
	 * Save callbacks and check if we are Desktop Safari
	 * @param {Object} callbacks
	 * @property {Function<Files[]>} onsuccess  A handler that is passed an array of files that were dropped
	 * @property {Function} onerror  A handler that is fired when there is an IO error
	 */
	constructor(callbacks) {
		this.callbacks = callbacks;
		this.isDesktopSafari =
			navigator.userAgent.match(/Macintosh.+Safari\/\d+/) && !navigator.userAgent.match(/Chrom(e|ium)/);
	}

	/**
	 * Alert a message only once per page load.
	 * It is used to inform Safari users that it cannot handle dragging directories.
	 * @param {String} msg  The message to alert
	 */
	alertOnce(msg) {
		if (DropHandler.hasAlerted) {
			return;
		}
		DropHandler.hasAlerted = true;
		alert(msg);
	}

	/**
	 * Check if the file might be a folder even though it looks like a file.
	 * Desktop Safari files that have no mime type are suspect.
	 * @param {File} file  The file to check
	 */
	mightBeFolder(file) {
		return this.isDesktopSafari && file.type === '';
	}

	/**
	 * Read a directory and add all files and subdirectories
	 * @param {File[]} filesToAdd  An array to collect valid files
	 * @param {DirectoryEntry} dir  The directory to collect
	 * @param {Function} counter  A function to count the number of entries processed
	 */
	collectDir(filesToAdd, dir, counter) {
		const handleEntries = entries => {
			this.collectEntries(filesToAdd, entries, counter);
			counter(-1);
		};
		dir.createReader().readEntries(handleEntries, this.callbacks.onerror);
	}

	/**
	 * Add an array of entries
	 * @param {File[]} filesToAdd  An array to collect valid files
	 * @param {FileEntry[]|DirectoryEntry[]} entries  The list of entries to collect
	 * @param {Function} counter  A function to count the number of entries processed
	 */
	collectEntries(filesToAdd, entries, counter) {
		counter(entries.length);
		const handleEntry = entry => {
			this.collectEntry(filesToAdd, entry, counter);
		};
		[].forEach.call(entries, handleEntry);
	}

	/**
	 * Add a single entry; if directory recurse; if file, add file
	 * @param {File[]} filesToAdd  An array to collect valid files
	 * @param {FileEntry|DirectoryEntry} entry  The entry to collect
	 * @param {Function} counter  A function to count the number of entries processed
	 */
	collectEntry(filesToAdd, entry, counter) {
		if (entry && entry.isDirectory) {
			this.collectDir(filesToAdd, entry, counter);
		} else if (entry && entry.isFile) {
			const handleFile = file => {
				file.fullPath = entry.fullPath;
				this.collectFile(filesToAdd, file, counter);
			};
			entry.file(handleFile, this.callbacks.onerror);
		}
	}

	/**
	 * Add a single file
	 * @param {File[]} filesToAdd  An array to collect valid files
	 * @param {File} file  The file to add
	 * @param {Function} counter  A function to count the number of entries processed
	 */
	collectFile(filesToAdd, file, counter) {
		// Double check that a folder is not masquerading as a file
		if (this.mightBeFolder(file)) {
			// trying to read a folder as a file will produce an error
			const reader = new FileReader();
			reader.onload = () => {
				// it's a file
				filesToAdd.push(file);
				counter(-1);
			};
			reader.onerror = () => {
				// it's a folder
				counter(-1);
				this.alertOnce('Safari does not support uploading folders.');
			};
			reader.readAsDataURL(file);
		} else {
			if (file.size > 0 && file.name && file.name !== '.DS_Store') {
				filesToAdd.push(file);
			}
			counter(-1);
		}
	}

	/**
	 * Process all the files associated with the given drop event.
	 * When complete, it calls the onerror or onsuccess callbacks sent in the constructor.
	 * @param {Event} evt  The drop event
	 */
	process(evt) {
		if (!evt.dataTransfer) {
			// not a valid drop
			return;
		}
		// collect the files we want to add
		const filesToAdd = [];
		// use a counter so that we only add files after we've recursively walked the entire tree
		let count = 0;
		const counter = add => {
			count += add;
			if (count === 0) {
				this.callbacks.onsuccess(filesToAdd);
			}
		};
		// `items` with `webkitGetAsEntry()` works in Firefox 50+, Chrome 30+ and Edge 14+
		// see https://caniuse.com/#feat=input-file-directory
		if (evt.dataTransfer.items && evt.dataTransfer.items[0] && evt.dataTransfer.items[0].webkitGetAsEntry) {
			// start out the equal to the number of items
			counter(evt.dataTransfer.items.length);
			// pass counter down into handlers
			[].forEach.call(evt.dataTransfer.items, item => {
				this.collectEntry(filesToAdd, item.webkitGetAsEntry(), counter);
			});
		}
		// IE11 and Safari only support `files`
		else if (evt.dataTransfer.files && evt.dataTransfer.files.length) {
			// start out the count equal to the number of files
			counter(evt.dataTransfer.files.length);
			// pass counter into handlers
			[].forEach.call(evt.dataTransfer.files, file => {
				this.collectFile(filesToAdd, file, counter);
			});
		}
	}
}
